/**
 * TrendGalleryBlock — блок «Галерея» зоны трендов (секция trend_gallery, 16.09).
 *
 * Мозаика карточек зоны (masonry, как прежняя галерея трендов), но обычным блоком конструктора:
 * админ ставит его в любую зону и в любое место среди лент. Заголовок-разделитель со счётчиком,
 * лимит «сначала N» с кнопкой «Показать ещё» — состав и порядок задаёт страница.
 */
import { useMemo, useState } from 'react'

import CardGallery from '../../../components/CardGallery.tsx'
import type { GenerationCard } from '../../../types/generationCard.ts'
import { backendCardToGalleryCard } from '../utils/cardGalleryItem.ts'
import type { BackendCard } from '../utils/popularCardApply.ts'

interface Props {
	title: string
	cards: BackendCard[]
	/** Сколько плиток показать сразу; 0 — все. */
	limit: number
	/** Контекст лендинга плиток (/bots/trends/<ctx>/tool/<slug>) — зона и подраздел не перескакивают. */
	landingCtx: string | null
	onPick: (raw: BackendCard) => void
	/** Мульти-тренд (23.10): клик по кадру версии на плитке — группа сразу с этой версией. */
	onPickMember?: (raw: BackendCard, memberId: string) => void
	/** Стафф-превью: блок скрыт глазиком — видим с пометкой. */
	hiddenPreview?: boolean
}

export default function TrendGalleryBlock({ title, cards, limit, landingCtx, onPick, onPickMember, hiddenPreview = false }: Props) {
	const [open, setOpen] = useState(false)
	const { items, rawById } = useMemo(() => {
		const rawById = new Map<string, BackendCard>()
		const items: GenerationCard[] = cards.map((card) => {
			const gc = backendCardToGalleryCard(card, 'trends', landingCtx)
			rawById.set(gc.id, card)
			return gc
		})
		return { items, rawById }
	}, [cards, landingCtx])
	const shown = open || limit <= 0 ? items : items.slice(0, limit)
	const hidden = items.length - shown.length
	if (items.length === 0) return null
	return (
		<section className={`bots-trends-zone-gallery${hiddenPreview ? ' bots-feed-blocks__hidden' : ''}`}>
			{hiddenPreview && <div className="bots-feed-blocks__hidden-tag">скрыт — видите только вы (превью)</div>}
			<div className="bots-trends-zone-gallery__head">
				<h2>{title}</h2>
				<span>{items.length}</span>
			</div>
			<div className="bots-trends-gallery bots-trends-gallery--block">
				<CardGallery
					items={shown}
					layout="masonry"
					cardClickAction="primary"
					onPrimaryAction={(gc) => {
						const raw = rawById.get(gc.id)
						if (raw) onPick(raw)
					}}
					onPickTrendMember={onPickMember ? (gc, memberId) => {
						const raw = rawById.get(gc.id)
						if (raw) onPickMember(raw, memberId)
					} : undefined}
				/>
			</div>
			{hidden > 0 && (
				<button type="button" className="bots-feed-block__more" onClick={() => setOpen(true)}>
					Показать ещё {hidden}
				</button>
			)}
		</section>
	)
}
